/**
 * VIKKI — email-draft + contact-form prefill builders
 *
 * Never auto-sends. Returns plain strings + mailto: URLs. The visitor
 * is the one who clicks the final Send button in their mail client or
 * the SALAR contact form.
 */

import { SALAR_PUBLIC_FACTS } from '../data/vikkiKnowledge';

const SUBJECTS_BY_TOPIC = {
  general:      'SALAR Website Inquiry',
  capability:   'SALAR Website Inquiry - Capabilities',
  architecture: 'SALAR Website Inquiry - Total Solution Architecture',
  patents:      'Patent Portfolio Inquiry - SALAR Technologies LLC',
  government:   'Government / Defense Inquiry - SALAR Technologies LLC',
  compliance:   'Compliance Inquiry - SALAR Technologies LLC',
  'ai-fabric':  'AI Fabric Assurance Inquiry - SALAR Technologies LLC',
  mission:      'Mission Assurance Inquiry - SALAR Technologies LLC',
  demo:         'SALAR Resilience Terrain Demo - Follow-up',
  resources:    'SALAR Resources / Publications Inquiry',
  about:        'SALAR Website Inquiry - About / Background',
  updates:      'SALAR News / Updates Inquiry',
  contact:      'SALAR Website Inquiry',
  private:      'SALAR Website Inquiry - General',
};

const INTEREST_BY_TOPIC = {
  capability:   'capability fit / engineering discussion',
  architecture: 'Total Solution Architecture review',
  patents:      'patent / licensing / collaboration',
  government:   'government / defense opportunity',
  compliance:   'compliance / safeguarding question',
  'ai-fabric':  'AI Fabric readiness / infrastructure',
  mission:      'mission electronics / link assurance',
  demo:         'interactive demo / scoring follow-up',
  resources:    'publications / capability materials',
  about:        'general inquiry / company background',
  updates:      'news / updates follow-up',
  contact:      'general inquiry',
  private:      'general inquiry',
  general:      'general inquiry',
};

/**
 * Build an email draft for the visitor.
 * Returns { to, cc, subject, bodyPlain, bodyHtml, mailto }.
 */
export function buildEmailDraft({ topic = 'general', question = '', visitorName = '' } = {}) {
  const subject = SUBJECTS_BY_TOPIC[topic] || SUBJECTS_BY_TOPIC.general;
  const interest = INTEREST_BY_TOPIC[topic] || INTEREST_BY_TOPIC.general;
  const cleanQuestion = String(question || '').trim().slice(0, 600) || '[describe your question or topic]';

  const bodyPlain =
`Hello SALAR Technologies team,

I visited salartechnologies.com and would like to ask about:
${cleanQuestion}

My interest is related to: ${interest}

Could someone please follow up with more information?

Name: ${visitorName || ''}
Organization:
Email:
Phone:
Message:

Thank you.`;

  const mailto = buildMailtoUrl({
    to: SALAR_PUBLIC_FACTS.emails.general,
    cc: SALAR_PUBLIC_FACTS.emails.primary,
    subject,
    body: bodyPlain,
  });

  return {
    to: SALAR_PUBLIC_FACTS.emails.general,
    cc: SALAR_PUBLIC_FACTS.emails.primary,
    subject,
    bodyPlain,
    mailto,
  };
}

export function buildMailtoUrl({ to, cc, subject, body }) {
  const params = [];
  if (cc)      params.push(`cc=${encodeURIComponent(cc)}`);
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  if (body)    params.push(`body=${encodeURIComponent(body)}`);
  const query = params.length ? `?${params.join('&')}` : '';
  return `mailto:${encodeURIComponent(to).replace(/%40/g, '@')}${query}`;
}

/**
 * Build a /contact prefill URL.
 * Contact.jsx reads ?topic=&subject=&message= via URLSearchParams.
 */
export function buildContactPrefillUrl({ topic = 'general', question = '' } = {}) {
  const subject = SUBJECTS_BY_TOPIC[topic] || SUBJECTS_BY_TOPIC.general;
  const interest = INTEREST_BY_TOPIC[topic] || INTEREST_BY_TOPIC.general;
  const cleanQuestion = String(question || '').trim().slice(0, 600);

  const message = cleanQuestion
    ? `Interest: ${interest}\n\nQuestion: ${cleanQuestion}`
    : `Interest: ${interest}`;

  const params = new URLSearchParams();
  params.set('topic', topic);
  params.set('subject', subject);
  params.set('message', message);
  return `/contact?${params.toString()}`;
}

/**
 * Map topic → contact-form `inquiryType` enum used by Contact.jsx Select.
 * Values must match `inquiryCategories` in /src/data/mockData.js exactly.
 * Falls back to 'General Inquiry'.
 */
export const TOPIC_TO_INQUIRY_TYPE = {
  capability:   'Technical / Research Inquiry',
  architecture: 'Technical / Research Inquiry',
  patents:      'Partnership / Collaboration',
  licensing:    'Partnership / Collaboration',
  government:   'Government / Defense Inquiry',
  compliance:   'Government / Defense Inquiry',
  'ai-fabric':  'Technical / Research Inquiry',
  mission:      'Technical / Research Inquiry',
  demo:         'Technical / Research Inquiry',
  resources:    'General Inquiry',
  about:        'General Inquiry',
  updates:      'General Inquiry',
  contact:      'General Inquiry',
  private:      'General Inquiry',
  general:      'General Inquiry',
  'AWS Marketplace / Thermal Resilience': 'AWS Marketplace / Thermal Resilience Assessment Inquiry',
  'aws-marketplace':                       'AWS Marketplace / Thermal Resilience Assessment Inquiry',
  thermal:                                 'AWS Marketplace / Thermal Resilience Assessment Inquiry',
};

/**
 * VikkiInquiry — admin-only schema, NOT shown to the public visitor.
 *
 * Phase 1: created in memory inside the widget and exposed on
 *   window.__vikkiInquiries for debugging only. No server persistence.
 * Phase 2: post to a server-side /api/inquiry endpoint that is admin-
 *   authenticated. The visitor never sees confidence scores, risk
 *   flags, or suggested internal answers.
 *
 * @typedef {Object} VikkiInquiry
 * @property {string} id
 * @property {string} visitorQuestion
 * @property {string} pageOfOrigin                URL path where asked
 * @property {string} timestamp                   ISO 8601
 * @property {string[]} publicPagesSearched
 * @property {string[]} matchedKeywords
 * @property {'Low'|'Medium'|'High'} confidence
 * @property {string} suggestedPublicSafeReply
 * @property {string|null} recommendedPagePath
 * @property {string} recommendedEmailSubject
 * @property {string} recommendedEmailDraft
 * @property {Object} riskFlags
 * @property {boolean} riskFlags.internalStrategyRisk
 * @property {boolean} riskFlags.exportControlledOrCUIRisk
 * @property {boolean} riskFlags.privateAddressOrContactRisk
 * @property {boolean} riskFlags.governmentEndorsementRisk
 * @property {boolean} riskFlags.legalOrProcurementAdviceRisk
 * @property {boolean} riskFlags.requiresHumanReview
 */
export function buildInquiryRecord({
  visitorQuestion,
  pageOfOrigin,
  pagesSearched = [],
  matchedKeywords = [],
  confidence = 'Low',
  suggestedReply = '',
  recommendedPage = null,
  topic = 'general',
} = {}) {
  const q = String(visitorQuestion || '');
  const draft = buildEmailDraft({ topic, question: q });

  return {
    id: `inq_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    visitorQuestion: q.slice(0, 1000),
    pageOfOrigin: String(pageOfOrigin || ''),
    timestamp: new Date().toISOString(),
    publicPagesSearched: pagesSearched.slice(0, 10),
    matchedKeywords: matchedKeywords.slice(0, 20),
    confidence,
    suggestedPublicSafeReply: suggestedReply,
    recommendedPagePath: recommendedPage,
    recommendedEmailSubject: draft.subject,
    recommendedEmailDraft: draft.bodyPlain,
    riskFlags: {
      internalStrategyRisk:        /\b(internal|strategy|roadmap|unreleased)\b/i.test(q),
      exportControlledOrCUIRisk:    /\b(cui|export.controlled|itar|ear|classified)\b/i.test(q),
      privateAddressOrContactRisk:  /\b(home address|personal phone)\b/i.test(q),
      governmentEndorsementRisk:    /\b(approved|certified|endorsed|cleared|partner)\b/i.test(q),
      legalOrProcurementAdviceRisk: /\b(legal|attorney|procurement advice|contract terms)\b/i.test(q),
      requiresHumanReview:          true,
    },
  };
}
